import { type PayloadAction, createReducer } from '@reduxjs/toolkit';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import { processToggleDisablePublicDownload, processToggleSeo } from 'reactApp/modules/home/home.helpers';
import {
    addToFavoritesSuccess,
    publishWeblink,
    removeFileSuccess,
    removeFromFavoritesSuccess,
    renameItemSuccess,
    toggleDisablePublicDownload,
    toggleSeo,
    unPublishWeblink,
} from 'reactApp/modules/modifying/modifying.actions';
import type {
    IAddToFavoritesSuccess,
    IRemoveFromFavoritesSuccess,
    IRenameItemSuccess,
    RemoveFileSuccessAction,
    ToggleSeoData,
    UnpublishWeblinkAction,
    UpdateWeblinkAction,
} from 'reactApp/modules/modifying/modifying.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { IPublicInfo } from 'reactApp/types/Tree';

import {
    addFilesToAlbumSuccess,
    createAlbumSuccess,
    loadAlbumFail,
    loadAlbumsListSuccess,
    loadAlbumSuccess,
    resetAllSelectedItems,
    selectAllItems,
    setUploadAlbumId,
    toggleItem,
} from './albums.actions';
import { CUSTOM_ALBUMS_ID } from './albums.constants';
import { isAlbumAllowedExtension, normalizeAlbumItem, normalizeCustomAlbum } from './albums.helpers';
import {
    type AlbumItem,
    type IAddItems,
    type IAlbum,
    type IAlbumId,
    type IAlbumsState,
    type ICreateAlbumSuccess,
    type ILoadAlbumsListSuccess,
    type ILoadAlbumSuccess,
    type SelectAllItems,
    type ToogleItem,
    isAlbum,
} from './albums.types';

const initialState: IAlbumsState = {
    list: {
        [CUSTOM_ALBUMS_ID]: {
            childs: [],
            isLoaded: false,
            isLoading: true,
            type: 'album',
            id: CUSTOM_ALBUMS_ID,
            name: 'Мои альбомы',
            parent: '',
            storage: EStorageType.albums,
            isFolder: true,
            kind: 'album',
        },
    },
    selectedItems: {},
};

export const albumReducer = createReducer(initialState, {
    [createAlbumSuccess.type]: (state, action: PayloadAction<ICreateAlbumSuccess>) => {
        const { id, name } = action.payload;
        const parent = state.list[CUSTOM_ALBUMS_ID];

        if (!isAlbum(parent)) {
            return;
        }

        state.list[id] = {
            id,
            storage: EStorageType.albums,
            name,
            flags: { isMedia: true },
            parent: CUSTOM_ALBUMS_ID,
            isLoaded: false,
            isLoading: true,
            childs: [],
            isFolder: true,
            type: 'album',
            kind: 'album',
        };

        parent.childs.unshift(id);
    },
    [publishWeblink.type]: (state, action: PayloadAction<UpdateWeblinkAction>) => {
        const { id, weblink, uniqueId } = action.payload;
        const item = state.list[id];

        if (!item || !isAlbum(item)) {
            return;
        }

        item.weblink = weblink;
        item.uniqueId = uniqueId;
    },
    [loadAlbumFail.type]: (state, action: PayloadAction<IAlbumId>) => {
        const { albumId } = action.payload;
        const item = state.list[albumId];

        if (!item || !isAlbum(item)) {
            state.list[albumId] = {
                id: albumId,
                storage: EStorageType.albums,
                error: true,
                flags: { isMedia: true },
                parent: CUSTOM_ALBUMS_ID,
                isLoaded: false,
                isLoading: true,
                childs: [],
                isFolder: true,
                type: 'album',
                name: '',
                kind: 'album',
            };

            return;
        }

        item.error = true;
    },
    [unPublishWeblink.type]: (state, action: PayloadAction<UnpublishWeblinkAction>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item || !isAlbum(item)) {
                return;
            }

            item.weblink = '';
        });
    },
    [loadAlbumsListSuccess.type]: (state, action: PayloadAction<ILoadAlbumsListSuccess>) => {
        const { list, cursor } = action.payload;
        const childs: string[] = [];

        list.forEach((item) => {
            state.list[item.id] = normalizeCustomAlbum(item);
            childs.push(item.id);
        });

        state.list[CUSTOM_ALBUMS_ID] = {
            cursor,
            childs,
            isLoaded: true,
            isLoading: false,
            type: 'album',
            id: CUSTOM_ALBUMS_ID,
            storage: EStorageType.albums,
            name: 'Мои альбомы',
            isFolder: true,
            parent: '',
            kind: 'album',
        };
    },
    [loadAlbumSuccess.type]: (state, action: PayloadAction<ILoadAlbumSuccess>) => {
        const { list, cursor, albumId, title, photos_count } = action.payload;
        const parent = state.list[albumId] as IAlbum;
        const childs: string[] = [...(parent?.childs || [])];

        list.forEach((item) => {
            const album = normalizeAlbumItem(item, albumId);

            state.list[album.id] = album;
            childs.push(album.id);
        });

        const weblinkInfo = normalizePublicApiInfo(action.payload) as IPublicInfo;

        state.list[albumId] = {
            ...parent,
            id: albumId,
            cursor,
            childs,
            isLoaded: true,
            isLoading: false,
            hasMoreToLoad: Boolean(cursor),
            storage: EStorageType.albums,
            parent: CUSTOM_ALBUMS_ID,
            type: 'album',
            name: title,
            kind: 'album',
            flags: { isMedia: true },
            photosCount: photos_count,
            ...weblinkInfo,
        };
    },
    [removeFromFavoritesSuccess.type]: (state, action: PayloadAction<IRemoveFromFavoritesSuccess>) => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item || isAlbum(item)) {
                return;
            }

            item.isInFavorites = false;
        });
    },
    [addToFavoritesSuccess.type]: (state, action: PayloadAction<IAddToFavoritesSuccess>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item || isAlbum(item)) {
                return;
            }

            item.isInFavorites = true;
        });
    },
    [renameItemSuccess.type]: (state, action: PayloadAction<IRenameItemSuccess>) => {
        const {
            newItem: { name },
            oldId: id,
        } = action.payload;

        const item = state.list[id];

        if (!item) {
            return;
        }

        state.list[id] = {
            ...item,
            name,
        };
    },
    [removeFileSuccess.type]: (state, action: PayloadAction<RemoveFileSuccessAction>) => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];
            const parent = state.list[item?.parent || ''];

            if (!item || !parent || !isAlbum(parent)) {
                return;
            }

            parent.childs = parent.childs.filter((child) => child !== id);
            delete state.list[id];
        });
    },
    [addFilesToAlbumSuccess.type]: (state, action: PayloadAction<IAddItems>) => {
        const { albumId, items } = action.payload;
        const album = state.list[albumId];

        if (!isAlbum(album)) {
            return;
        }

        const childs = [...album.childs];

        items.forEach((item) => {
            const ext = getExtension(item);

            if (item.storage !== EStorageType.albums || !isAlbumAllowedExtension(ext)) {
                return;
            }

            state.list[item.id] = item;
            childs.push(item.id);
        });

        album.childs = childs;
    },
    [setUploadAlbumId.type]: (state, action: PayloadAction<IAlbumId>) => {
        state.currentUploadAlbum = action.payload.albumId;
    },
    [toggleItem.type]: (state, action: PayloadAction<ToogleItem>) => {
        const { selectedItem } = action.payload;
        const selectedItems = { ...state.selectedItems };

        if (selectedItems[selectedItem.id]) {
            delete selectedItems[selectedItem.id];
        } else {
            selectedItems[selectedItem.id] = selectedItem;
        }

        return {
            ...state,
            selectedItems,
        };
    },
    [selectAllItems.type]: (state, action: PayloadAction<SelectAllItems>) => {
        const { allItems } = action.payload;
        const selectAll = Object.keys(state.selectedItems).length === Object.keys(allItems).length;

        return {
            ...state,
            selectedItems: selectAll ? {} : allItems,
        };
    },
    [resetAllSelectedItems.type]: (state) => {
        return {
            ...state,
            selectedItems: {},
        };
    },
    [toggleSeo.type]: (state, action: PayloadAction<ToggleSeoData>): void => {
        processToggleSeo<AlbumItem>(state.list[action?.payload?.item?.id]);
    },
    [toggleDisablePublicDownload.type]: (state, action: ReturnType<typeof toggleDisablePublicDownload>): void => {
        processToggleDisablePublicDownload<AlbumItem>(state.list[action?.payload?.item?.id], action?.payload.disableDownload);
    },
});
